import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Heart, ArrowRight, ArrowLeft, CheckCircle } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { Md } from '@/components/Md';

const API = process.env.REACT_APP_BACKEND_URL;

const CONTENT_BY_TYPE = {
  gravida: {
    title: 'Gravidez',
    messages: [
      'Não precisas de comer por dois, precisas de comer com mais intenção.',
      'Organizar refeições simples já é suficiente para ter impacto.',
      'O mais importante é consistência, não perfeição.',
    ],
  },
  pos_parto: {
    title: 'Pós-parto',
    messages: [
      'O teu corpo está em recuperação. A alimentação deve ajudar, não complicar.',
      'É normal haver mais fome e menos rotina.',
      'Vamos trabalhar com aquilo que é possível nesta fase.',
    ],
  },
  introducao_alimentar: {
    title: 'Introdução alimentar',
    messages: [
      'Não precisas de fazer tudo perfeito.',
      'O foco é exposição, não quantidade.',
      'A repetição é parte do processo.',
    ],
  },
  geral: {
    title: 'Alimentação',
    messages: [
      'O objetivo não é cortar tudo, é aprender a organizar.',
      'A consistência ao longo da semana é mais importante do que um dia isolado.',
      'Não existem alimentos proibidos, existem decisões mais conscientes.',
    ],
  },
};

function QuestionField({ q, value, onChange }) {
  if (q.type === 'text') return <Textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder="Escreva aqui..." className="rounded-2xl border-brand-border resize-none" rows={3} data-testid={`q-${q.key}`} />;
  if (q.type === 'boolean') return (
    <div className="flex gap-3">
      {['Sim', 'Não'].map(o => <button key={o} type="button" onClick={() => onChange(o === 'Sim')} className={`flex-1 px-4 py-3 rounded-2xl text-sm font-medium transition-all ${(value === true && o === 'Sim') || (value === false && o === 'Não') ? 'bg-brand-green text-white' : 'bg-white border border-brand-border text-brand-muted'}`} data-testid={`q-${q.key}-${o}`}>{o}</button>)}
    </div>
  );
  if (q.type === 'scale') return (
    <div>
      <div className="flex gap-1.5 justify-center">{[1, 2, 3, 4, 5].map(n => <button key={n} type="button" onClick={() => onChange(n)} className={`w-12 h-12 rounded-2xl text-sm font-semibold transition-all ${value === n ? 'bg-brand-green text-white' : 'bg-white border border-brand-border text-brand-muted'}`} data-testid={`q-${q.key}-${n}`}>{n}</button>)}</div>
      <div className="flex justify-between mt-1"><span className="text-[10px] text-brand-muted">{q.min_label || '1'}</span><span className="text-[10px] text-brand-muted">{q.max_label || '5'}</span></div>
    </div>
  );
  if (q.type === 'choice') return (
    <div className="flex flex-wrap gap-2">{(q.options || []).map(o => <button key={o} type="button" onClick={() => onChange(o)} className={`px-4 py-2.5 rounded-2xl text-sm transition-all ${value === o ? 'bg-brand-green text-white' : 'bg-white border border-brand-border text-brand-muted'}`} data-testid={`q-${q.key}-opt`}>{o}</button>)}</div>
  );
  if (q.type === 'choice_multiple') {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div className="flex flex-wrap gap-2">{(q.options || []).map(o => <button key={o} type="button" onClick={() => onChange(selected.includes(o) ? selected.filter(x => x !== o) : [...selected, o])} className={`px-4 py-2.5 rounded-2xl text-sm transition-all ${selected.includes(o) ? 'bg-brand-green text-white' : 'bg-white border border-brand-border text-brand-muted'}`} data-testid={`q-${q.key}-opt`}>{o}</button>)}</div>
    );
  }
  return <Input value={value || ''} onChange={e => onChange(e.target.value)} className="rounded-2xl border-brand-border" data-testid={`q-${q.key}`} />;
}

export default function OnboardingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [config, setConfig] = useState(null);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${API}/api/onboarding/config`); setConfig(data); } catch {}
    })();
  }, []);

  const patientType = user?.patient_type || 'geral';
  const typeContent = CONTENT_BY_TYPE[patientType] || CONTENT_BY_TYPE.geral;
  const questions = config?.questionnaire || [];
  const totalSteps = 5;

  const handleComplete = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/onboarding/complete`, { answers });
      try { sessionStorage.setItem('onboarding_done', 'true'); } catch {}
      window.location.href = '/dashboard';
    } catch { toast.error('Erro ao guardar'); }
    finally { setSaving(false); }
  };

  const handleSkip = async () => {
    await axios.post(`${API}/api/onboarding/skip`);
    try { sessionStorage.setItem('onboarding_done', 'true'); } catch {}
    window.location.href = '/dashboard';
  };

  const setAnswer = (key, val) => setAnswers(prev => ({ ...prev, [key]: val }));
  const progress = ((step + 1) / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col" data-testid="onboarding-page">
      {/* Progress bar */}
      <div className="h-1 bg-brand-border"><div className="h-full bg-brand-green transition-all duration-500" style={{ width: `${progress}%` }} /></div>

      {/* Skip */}
      <div className="flex justify-end px-4 pt-3">
        <button onClick={handleSkip} className="text-xs text-brand-muted hover:text-brand-text" data-testid="skip-onboarding">Saltar</button>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 max-w-md mx-auto w-full">

        {/* Step 0: Welcome */}
        {step === 0 && (
          <div className="text-center fade-in" data-testid="step-welcome">
            <div className="w-16 h-16 rounded-full bg-brand-gold/15 flex items-center justify-center mx-auto mb-6">
              <Heart size={28} weight="duotone" className="text-brand-gold" />
            </div>
            <h1 className="font-heading font-semibold text-2xl text-brand-text mb-4">Bem-vinda</h1>
            <div className="space-y-3 text-sm text-brand-muted leading-relaxed text-left">
              <p>Este é o teu espaço de acompanhamento.</p>
              <p>Aqui vais encontrar o teu plano, os teus registos e tudo o que precisas para organizar a tua alimentação no dia a dia.</p>
              <p className="text-brand-text font-medium">O objetivo não é fazer tudo perfeito, mas sim construir algo que funcione para ti.</p>
            </div>
          </div>
        )}

        {/* Step 1: How it works */}
        {step === 1 && (
          <div className="text-center fade-in" data-testid="step-how">
            <h2 className="font-heading font-semibold text-xl text-brand-text mb-6">Como funciona</h2>
            <div className="space-y-4 text-left">
              {[
                'Tens um plano alimentar adaptado a ti',
                'Podes usar a app para organizar o teu dia',
                'Tens um dia específico de feedback semanal',
                'O objetivo é ajustar ao longo do tempo',
              ].map((t, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-brand-mint/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-semibold text-brand-green">{i + 1}</span>
                  </div>
                  <p className="text-sm text-brand-text">{t}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Type-specific content */}
        {step === 2 && (
          <div className="text-center fade-in" data-testid="step-content">
            <span className="text-[10px] text-brand-green font-semibold uppercase tracking-wider">{typeContent.title}</span>
            <h2 className="font-heading font-semibold text-xl text-brand-text mt-2 mb-6">O que é importante saber</h2>
            <div className="space-y-4">
              {typeContent.messages.map((m, i) => (
                <div key={i} className="bg-white border border-brand-border rounded-2xl p-4 text-left">
                  <p className="text-sm text-brand-text leading-relaxed">{m}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Questionnaire */}
        {step === 3 && (
          <div className="w-full fade-in" data-testid="step-questionnaire">
            <h2 className="font-heading font-semibold text-xl text-brand-text text-center mb-2">Questionário inicial</h2>
            <p className="text-xs text-brand-muted text-center mb-6">Para conhecer melhor as tuas necessidades</p>
            <div className="space-y-6">
              {questions.map(q => (
                <div key={q.key}>
                  <Md as="p" className="text-sm font-medium text-brand-text mb-2">{q.label}</Md>
                  <QuestionField q={q} value={answers[q.key]} onChange={v => setAnswer(q.key, v)} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Finish */}
        {step === 4 && (
          <div className="text-center fade-in" data-testid="step-finish">
            <div className="w-16 h-16 rounded-full bg-brand-green/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={32} weight="duotone" className="text-brand-green" />
            </div>
            <h2 className="font-heading font-semibold text-xl text-brand-text mb-4">Está tudo pronto</h2>
            <div className="space-y-3 text-sm text-brand-muted leading-relaxed">
              <p>Agora vamos começar.</p>
              <p>O próximo passo é começares a aplicar o plano no teu dia a dia.</p>
              <p className="text-brand-text font-medium">Qualquer dúvida, vamos ajustando ao longo do acompanhamento.</p>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="px-6 pb-8 pt-4 max-w-md mx-auto w-full">
        <div className="flex gap-3">
          {step > 0 && (
            <button onClick={() => setStep(step - 1)} className="flex-1 border border-brand-border text-brand-muted rounded-full py-3.5 font-medium text-sm flex items-center justify-center gap-1" data-testid="prev-step">
              <ArrowLeft size={16} /> Anterior
            </button>
          )}
          {step < 4 ? (
            <button onClick={() => setStep(step + 1)} className="flex-1 bg-brand-green text-white rounded-full py-3.5 font-medium text-sm flex items-center justify-center gap-1" data-testid="next-step">
              Seguinte <ArrowRight size={16} />
            </button>
          ) : (
            <button onClick={handleComplete} disabled={saving} className="flex-1 bg-brand-green text-white rounded-full py-3.5 font-medium text-sm flex items-center justify-center gap-1 disabled:opacity-50" data-testid="complete-onboarding">
              {saving ? 'A guardar...' : 'Começar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
